import Box from '@mui/material/Box';
import Tooltip from '@mui/material/Tooltip';
import { alpha, useTheme } from '@mui/material/styles';
import AlternateEmailOutlined from '@mui/icons-material/AlternateEmailOutlined';
import NotificationsActiveOutlined from '@mui/icons-material/NotificationsActiveOutlined';
import StickyNote2 from '@mui/icons-material/StickyNote2';

import useLocalization from 'hooks/useLocalization';
import type { SystemNotificationVisual } from 'store/slices/system-notifications';
import WorkflowStatusIcon from 'views/chats/workflow/WorkflowStatusIcon';

const ICON_BOX = 32;
const ICON_SIZE = 22;

type Props = {
  visual?: SystemNotificationVisual;
};

/**
 * אותה שפה ויזואלית כמו `WorkflowStatusIcon` בהגדרות התראות / רשימת שיחות;
 * הערות ותיוג — StickyNote2 ו־AlternateEmail עם רקעים עקביים.
 */
export default function SystemNotificationLeadingIcon({ visual }: Props) {
  const theme = useTheme();
  const { currentLocale } = useLocalization();
  const notifLoc = currentLocale.settingsPopup.userConfig.tabs.notifications;

  if (visual?.kind === 'workflow') {
    return <WorkflowStatusIcon status={visual.status} size={ICON_SIZE} />;
  }

  if (visual?.kind === 'note') {
    return (
      <Tooltip title={notifLoc.noteInChatLabel} placement="top">
        <Box
          aria-label={notifLoc.noteInChatLabel}
          sx={{
            width: ICON_BOX,
            height: ICON_BOX,
            borderRadius: 1,
            flexShrink: 0,
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            bgcolor: alpha(theme.palette.info.main, 0.14),
            color: theme.palette.mode === 'dark' ? theme.palette.info.light : theme.palette.info.dark,
          }}
        >
          <StickyNote2 sx={{ fontSize: ICON_SIZE }} />
        </Box>
      </Tooltip>
    );
  }

  if (visual?.kind === 'tag') {
    return (
      <Tooltip title={notifLoc.taggedInNoteLabel} placement="top">
        <Box
          aria-label={notifLoc.taggedInNoteLabel}
          sx={{
            width: ICON_BOX,
            height: ICON_BOX,
            borderRadius: 1,
            flexShrink: 0,
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            bgcolor: alpha(theme.palette.secondary.main, theme.palette.mode === 'dark' ? 0.22 : 0.14),
            color: theme.palette.mode === 'dark' ? theme.palette.secondary.light : theme.palette.secondary.dark,
          }}
        >
          <AlternateEmailOutlined sx={{ fontSize: ICON_SIZE }} />
        </Box>
      </Tooltip>
    );
  }

  return (
    <Tooltip title={notifLoc.pageTitle} placement="top">
      <Box
        sx={{
          width: ICON_BOX,
          height: ICON_BOX,
          borderRadius: 1,
          flexShrink: 0,
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: alpha(theme.palette.grey[500], theme.palette.mode === 'dark' ? 0.28 : 0.16),
          color: theme.palette.mode === 'dark' ? theme.palette.grey[300] : theme.palette.grey[700],
        }}
      >
        <NotificationsActiveOutlined sx={{ fontSize: ICON_SIZE }} />
      </Box>
    </Tooltip>
  );
}
